/**
 * URL-scheme allowlist guard.
 *
 * Returns the given URL only when it is safe to use as an `href`:
 *   - absolute http:// or https:// URLs
 *   - mailto: and tel: links
 *   - app-internal relative paths (leading '/')
 *
 * Anything else — including dangerous schemes like `javascript:`, `data:`,
 * and `vbscript:` — is rejected and replaced with '#'. This blocks
 * URL-scheme XSS where server-controlled values (notification action URLs,
 * attachment links, infrastructure URL fields) are rendered as `<a href>`.
 *
 * Defensive: trims and lowercases before testing, tolerates null/undefined,
 * and fails closed (returns '#') on any unexpected input.
 */
export function safeHref(url: string | null | undefined): string {
  if (url == null) return '#';
  if (typeof url !== 'string') return '#';

  const trimmed = url.trim();
  if (trimmed === '') return '#';

  // Internal relative paths are always allowed (e.g. "/incidents/123").
  // Reject protocol-relative URLs ("//evil.com") which would resolve to an
  // external origin despite the leading slash.
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    return trimmed;
  }

  const lower = trimmed.toLowerCase();

  // Explicitly block known-dangerous schemes regardless of casing/whitespace.
  if (
    lower.startsWith('javascript:') ||
    lower.startsWith('data:') ||
    lower.startsWith('vbscript:')
  ) {
    return '#';
  }

  // Allow only the safe absolute schemes.
  if (
    lower.startsWith('http://') ||
    lower.startsWith('https://') ||
    lower.startsWith('mailto:') ||
    lower.startsWith('tel:')
  ) {
    return trimmed;
  }

  return '#';
}

/**
 * Relative-route guard for client-side router navigation.
 *
 * `navigate()` must only ever receive an app-internal path. Returns the URL
 * if it is a safe internal route (leading single '/'), otherwise null so the
 * caller can ignore it. Protocol-relative ("//host") and absolute URLs are
 * rejected — external destinations should never be passed to the router.
 */
export function safeInternalPath(url: string | null | undefined): string | null {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null;
  return trimmed;
}
